import { Fragment, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import type { BoardFreshnessDivision } from '@erp/rpc';
import { formatDateTime } from '@erp/shared-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  assessBoardFreshness,
  type BoardFreshness,
  type RunFreshness,
  type RunGroup,
  type StockFreshness,
} from './boardFreshness';
import { useMinuteClock } from './useMinuteClock';

interface BoardFreshnessIndicatorProps {
  divisions: BoardFreshnessDivision[];
  /**
   * Admins also get each run's outcome and the stock feed's times per
   * division, in a tooltip; everyone else sees only when the board last
   * updated.
   */
  detailed: boolean;
}

/** Month, day and time, no year: short enough for the header line. */
function formatShort(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface FreshnessEntryProps {
  names: string[];
  time: string | null;
  note: string | null;
  /** Name the divisions only when they differ across entries. */
  withNames: boolean;
}

function FreshnessEntry({ names, time, note, withNames }: FreshnessEntryProps) {
  return (
    <span className="whitespace-nowrap">
      {withNames && <span>{names.join(', ')}: </span>}
      {time !== null && <span className="tabular-nums">{time}</span>}
      {note !== null && (
        <span className="font-medium">
          {time !== null ? ' ' : ''}({note})
        </span>
      )}
    </span>
  );
}

function joinEntries(items: { key: string; node: ReactNode }[]) {
  return items.map((item, i) => (
    <Fragment key={item.key}>
      {i > 0 && '; '}
      {item.node}
    </Fragment>
  ));
}

/** The admin tooltip: every run's outcome and stock time, per division. */
function FreshnessDetail({
  runs,
  stock,
}: Pick<BoardFreshness, 'runs' | 'stock'>) {
  const gt = useGT();
  const runNote: Record<RunFreshness, string | null> = {
    current: null,
    failed: gt('failed', { context: 'buying board freshness: run failed' }),
    unfinished: gt('not finished', {
      context: 'buying board freshness: run still running or stopped',
    }),
    unrecorded: gt('finish not recorded', {
      context: 'buying board freshness: run predates outcome tracking',
    }),
    not_today: gt('not today', {
      context: 'buying board freshness: run is from an earlier day',
    }),
    missing: gt('no run recorded', {
      context: 'buying board freshness: no replenishment run yet',
    }),
  };
  const stockNote: Record<StockFreshness, string | null> = {
    current: null,
    not_today: gt('not today', {
      context: 'buying board freshness: stock is from an earlier day',
    }),
    missing: gt('no stock data', {
      context: 'buying board freshness: no stock snapshot for the division',
    }),
  };
  const runTime = (group: RunGroup) => {
    const run = group.run;
    if (run === null) return null;
    if (run.completedAt !== null) return formatDateTime(run.completedAt);
    return gt('started {time}', {
      context: 'buying board freshness: run start time, no end recorded',
      time: formatDateTime(run.startedAt),
    });
  };

  return (
    <div className="space-y-1">
      <div>
        {gt('Last replenishment run:', {
          context: 'buying board freshness label',
        })}{' '}
        {joinEntries(
          runs.map((g) => ({
            key: g.key,
            node: (
              <FreshnessEntry
                names={g.divisionNames}
                time={runTime(g)}
                note={runNote[g.state]}
                withNames={runs.length > 1}
              />
            ),
          })),
        )}
      </div>
      <div>
        {gt('Stock updated:', { context: 'buying board freshness label' })}{' '}
        {joinEntries(
          stock.map((g) => ({
            key: g.key,
            node: (
              <FreshnessEntry
                names={g.divisionNames}
                time={g.syncedAt === null ? null : formatDateTime(g.syncedAt)}
                note={stockNote[g.state]}
                withNames={stock.length > 1}
              />
            ),
          })),
        )}
      </div>
    </div>
  );
}

/**
 * When the board's data last updated, inline in the Filters header, with a
 * text warning when the last completed run or any stock update is not from
 * today (GEN-3149).
 */
export function BoardFreshnessIndicator({
  divisions,
  detailed,
}: BoardFreshnessIndicatorProps) {
  const gt = useGT();
  const now = useMinuteClock();
  if (divisions.length === 0) return null;
  const { runs, stock, stale, lastUpdatedAt, notUpdatedToday } =
    assessBoardFreshness(divisions, now);

  const updated =
    lastUpdatedAt === null
      ? gt('No update recorded', {
          context: 'buying board freshness: no completed refresh yet',
        })
      : notUpdatedToday
        ? gt('Not updated today (last {time})', {
            context:
              'buying board freshness warning: data is from an earlier day',
            time: formatShort(lastUpdatedAt),
          })
        : gt('Updated {time}', {
            context: 'buying board freshness: when the data last refreshed',
            time: formatShort(lastUpdatedAt),
          });
  // Admins are also warned when today's data stands but the latest run
  // failed or did not finish; the tooltip says which.
  const warn = notUpdatedToday || (detailed && stale);

  const label = (
    <span
      role="status"
      tabIndex={detailed ? 0 : undefined}
      className={cn(
        'flex shrink-0 items-center gap-1 whitespace-nowrap',
        warn
          ? 'font-medium text-amber-700 dark:text-amber-400'
          : 'text-muted-foreground',
      )}
    >
      {warn && <WarningIcon aria-hidden className="h-3.5 w-3.5" />}
      {updated}
    </span>
  );
  if (!detailed) return label;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{label}</TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-md text-xs">
          <FreshnessDetail runs={runs} stock={stock} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
